// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useTranslation from "@/hooks/useTranslation";
import Icon from "@/UI/atoms/Icon";
import "./styles.css";
// --- Constants ----------------------------------------------------------------------------------
/** A real-looking company rather than `yourslug`, so the example reads as a catalog somebody could open. */
const EXAMPLE_SLUG = "al-rabwah";
// --- Logic --------------------------------------------------------------------------------------
type Props = { onClose: () => void };

/**
 * What the public address is, for whoever is filling the field in.
 *
 * "Slug" is jargon, and this one is not an internal key: it is the company's public catalog address,
 * printed on whatever they hand to buyers. So the example comes first — the word shown inside a real
 * URL with the chosen part picked out — then what changing it costs, then the rules. In that order,
 * because the example teaches faster than the rules do.
 *
 * **It is editable, and that is the whole reason this popup exists.** A typo in somebody's public
 * address is worth fixing, and every link already shared is worth not breaking by accident.
 */
export default function SlugInfoPopup({ onClose }: Props) {
    const t = useTranslation();

    return (
        <section className="popup-card slug-info">
            <header className="slug-info__head">
                <span className="slug-info__head-icon">
                    <Icon name="info" size={20} />
                </span>
                <div className="slug-info__head-text">
                    <h2 className="slug-info__title">{t.slugInfo.title}</h2>
                    <p className="slug-info__definition">{t.slugInfo.definition}</p>
                </div>
            </header>

            <div className="slug-info__example">
                <span className="slug-info__example-label">{t.slugInfo.exampleLabel}</span>

                {/* Always latin-ordered: an RTL page would otherwise scatter the parts of the URL. */}
                <p className="slug-info__url" dir="ltr">
                    <span className="slug-info__url-slug">{EXAMPLE_SLUG}</span>
                    <span className="slug-info__url-rest">.{constants.catalogDomain}</span>
                </p>

                <div className="slug-info__pointer" dir="ltr">
                    <span className="slug-info__pointer-mark" />
                    <span className="slug-info__pointer-text">{t.slugInfo.exampleCaption}</span>
                </div>
            </div>

            {/* The consequence, beside the thing that causes it. */}
            <div className="slug-info__note">
                <Icon name="link" size={16} />
                <p>{t.slugInfo.changeNote}</p>
            </div>

            <div className="slug-info__rules">
                <span className="slug-info__rules-title">{t.slugInfo.rulesTitle}</span>
                <ul className="slug-info__rules-list">
                    {t.slugInfo.rules.map((rule) => (
                        <li key={rule} className="slug-info__rule">
                            <span className="slug-info__rule-tick">
                                <Icon name="check" size={12} />
                            </span>
                            <span className="slug-info__rule-text">{rule}</span>
                        </li>
                    ))}
                </ul>
            </div>

            <button type="button" className="slug-info__button" onClick={onClose}>
                {t.slugInfo.button}
            </button>
        </section>
    );
}
